import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../lib/react-native-compat';
import {
    selectedJobAtom, selectedJobDataAtom, userEmailAtom, userNameAtom,
    jobUserDataAtom, jobCostDetailAtom, jobPhaseCodesAtom, sideMenuBudgetsAtom,
    mechanicallyCompleteAtom,
} from '../store/company';
import {
    loadProjectSchedule, uploadProjectSchedule, uploadProductivityBaseline,
} from '../db/company';
import { readFileBase64 } from './scheduleUploadCard';
import WeeklyUpdate from './weeklyUpdate';
import ScheduleMapping from './scheduleMapping';
import ScheduleBaselineReview from './scheduleBaselineReview';
import { requiredLevel, achievedLevel, validateSchedule } from './scheduleDetail';

// Schedule tab.
//
// The one place a job's plan is set up and kept current, in two views:
//
//   Baseline      — upload the file pair (the scheduler's P6 / MS Project
//                   export and the productivity baseline workbook), see how
//                   much schedule detail the job's size actually requires, and
//                   map the schedule's activities onto the budget's cost codes.
//   Weekly Update — every week, revise the plan in a grid seeded from that
//                   mapping and save it against a week-ending date. The save
//                   becomes the active quantity schedule the daily field report
//                   paces against.
//
// The validator is the reason the Baseline view is not just two upload boxes.
// A $40M job planned as six bars passes every check the uploads can make and is
// still useless for pacing anything, so the required detail level is derived
// from the job's contract revenue and the schedule is measured against it.

// The review modal. Uploading a schedule is the moment the mapping is worth
// looking at — every date on the job is about to be re-driven from it — so the
// panel comes to the PM instead of sitting on the page waiting to be expanded.
const backdrop = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 24,
    overflowY: 'auto',
};

const sheet = {
    width: '100%',
    maxWidth: 1320,
    maxHeight: '92vh',
    overflowY: 'auto',
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.3)',
};

const BaselineWeekly = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [userEmail] = useAtom(userEmailAtom);
    const [userName] = useAtom(userNameAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [smBudgets] = useAtom(sideMenuBudgetsAtom);
    // The contract mechanical-completion date, as entered on the Job Info tab.
    // It is the line the review grid's float answers to.
    const [mechanicallyComplete] = useAtom(mechanicallyCompleteAtom);

    const buId = selectedJobData?.BU_ID || selectedJob;
    const contractMC = mechanicallyComplete || null;
    const jobLabel = [buId, selectedJobData?.Job_Description].filter(Boolean).join(' · ');

    const [sub, setSub] = useState('weekly');          // 'baseline' | 'weekly'
    const [data, setData] = useState({ imports: [], activities: [], baseline: [], quantity: [] });
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);            // 'schedule' | 'baseline'
    const [errors, setErrors] = useState({});
    const [notices, setNotices] = useState({});
    const [viewImportId, setViewImportId] = useState(null);
    // The mapping panel's live rows, so the validator reacts to a re-bind
    // straight away instead of only after a save round-trip.
    const [mappingRows, setMappingRows] = useState([]);
    const [review, setReview] = useState(false);       // mapping review modal
    // The mapping panel owns the rows; the Gantt's gutter edits them through
    // this, so there is one mapping and one Save button rather than two.
    const mappingApi = useRef(null);
    const assignPhase = useCallback((key, costCode) => mappingApi.current?.assignActivity(key, costCode), []);
    const setPhaseQty = useCallback((costCode, qty) => mappingApi.current?.setQty(costCode, qty), []);

    const refresh = useCallback(async () => {
        if (!buId) return;
        setLoading(true);
        const res = await loadProjectSchedule({
            buId: String(buId),
            ...(viewImportId != null ? { quantityImportId: viewImportId } : {}),
        });
        setLoading(false);
        if (res && !res.error) {
            setData({
                imports: res.imports || [],
                activities: res.activities || [],
                baseline: res.baseline || [],
                quantity: res.quantity || [],
            });
            setErrors((p) => ({ ...p, page: null }));
        } else {
            setErrors((p) => ({ ...p, page: res?.error || 'Could not load this job’s schedule' }));
        }
    }, [buId, viewImportId]);

    useEffect(() => { refresh(); }, [refresh]);

    // A different job means whatever week was being viewed no longer applies.
    useEffect(() => { setViewImportId(null); setMappingRows([]); setReview(false); }, [buId]);

    // Escape closes the review, the way every other modal in the app behaves.
    useEffect(() => {
        if (!review) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setReview(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [review]);

    const activeOf = (kind) => (data.imports || []).find((i) => i.Kind === kind && i.Is_Active);

    const doUpload = async (kind, file, preError) => {
        setErrors((p) => ({ ...p, [kind]: preError || null }));
        setNotices((p) => ({ ...p, [kind]: null }));
        if (preError) return;
        if (String(file.name).toLowerCase().endsWith('.mpp')) {
            setErrors((p) => ({
                ...p,
                [kind]: 'MS Project .mpp files can’t be read directly. In MS Project use File → Save As → XML (*.xml) and upload that instead.',
            }));
            return;
        }
        setBusy(kind);
        try {
            const fileBase64 = await readFileBase64(file);
            const payload = {
                buId: String(buId),
                fileName: file.name,
                fileBase64,
                uploadedBy: userEmail || userName || null,
            };
            const res = kind === 'schedule'
                ? await uploadProjectSchedule(payload)
                : await uploadProductivityBaseline(payload);
            if (!res || res.error) {
                setErrors((p) => ({ ...p, [kind]: res?.error || 'Upload failed' }));
            } else {
                const warn = (res.warnings || []).join(' ');
                // How much of the mapping the file did for us is the first thing
                // worth knowing after an upload — it decides how much of the
                // panel below is a guess.
                const s = res.stats;
                const found = s
                    ? ` — phase code on ${s.withCostCode} of ${s.activities}, quantity on ${s.withQuantity}`
                    : '';
                setNotices((p) => ({
                    ...p,
                    [kind]: `Loaded ${Math.round(Number(res.count) || 0).toLocaleString()} rows${found}${warn ? ` — ${warn}` : ''}`,
                }));
                await refresh();
                // A new schedule re-drives every date on the job, so the
                // mapping is exactly what wants checking now.
                if (kind === 'schedule') setReview(true);
            }
        } catch (e) {
            setErrors((p) => ({ ...p, [kind]: e?.message || String(e) }));
        }
        setBusy(null);
    };

    // ── the validator's inputs, all off atoms already loaded ────────────────

    // Phase code → discipline, as entered on the Productivity Tracker tab. Same
    // source dayZeroBudget / cologUpload read, so disciplines stay consistent.
    const disciplineMap = useMemo(() => {
        const m = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab === 'Productivity Tracker' && d?.Forecast_Field_Name === 'discipline') {
                m[d?.Forecast_Row_ID] = d?.Forecast_Value;
            }
        });
        return m;
    }, [jobUserData]);

    // Cost codes to map against when no productivity baseline has been uploaded:
    // the phases this job's budget actually uses. Quantities aren't in here, so
    // the rows come through needing one — but the dates auto-map, which is the
    // hard half and the part only the schedule can answer.
    const budgetPhases = useMemo(() => {
        const seen = new Map();
        (jobCostDetail || []).forEach((row) => {
            const code = String(row?.Phase_Code || '').trim();
            if (!code || seen.has(code)) return;
            const desc = String(row?.Phase_Code_Description || jobPhaseCodes?.[code] || '').trim();
            if (!desc) return;                       // nothing to name-match on
            seen.set(code, { Cost_Code: code, Description: desc, UOM: '', Budget_Qty: 0, Budget_Hours: null });
        });
        return [...seen.values()].sort((a, b) => a.Cost_Code.localeCompare(b.Cost_Code));
    }, [jobCostDetail, jobPhaseCodes]);

    const required = useMemo(() => requiredLevel({
        revenue: (smBudgets?.contractValuesRevised || 0) + (smBudgets?.changeOrderRevenue || 0),
        jobTier: selectedJobData?.Cat_Code_BU_16,
    }), [smBudgets, selectedJobData]);

    // The budget's cost codes are what the schedule is measured against: the
    // saved quantity schedule once there is one, the baseline upload before.
    const budgetRows = useMemo(() => {
        const saved = (data.quantity || []).filter((r) => !r.Period_Date);
        return saved.length > 0 ? saved : (data.baseline || []).filter((r) => !r.Period_Date);
    }, [data.quantity, data.baseline]);

    const achieved = useMemo(() => achievedLevel({
        bindings: mappingRows,
        budgetRows,
        disciplineMap,
        activities: data.activities,
    }), [mappingRows, budgetRows, disciplineMap, data.activities]);

    const validation = useMemo(
        () => validateSchedule({ required, achieved }),
        [required, achieved],
    );

    if (!buId) {
        return <Text style={{ padding: 16, fontSize: 13, color: '#64748B' }}>Pick a job to set up its schedule.</Text>;
    }

    return (
        <View style={{ padding: 12 }}>
            {/* Page header — what this is on one line, the two views and the
                actions on the other, so the controls stop competing with the
                prose for the same row. */}
            <View style={{ marginBottom: 14 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <View style={{ flexDirection: 'row', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden' }}>
                        {[['baseline', 'Baseline'], ['weekly', 'Weekly Update']].map(([val, label]) => (
                            <Pressable
                                key={val}
                                onPress={() => setSub(val)}
                                style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14, backgroundColor: sub === val ? '#1D4ED8' : '#fff', cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 12, fontWeight: '700', color: sub === val ? '#fff' : '#475569' }}>{label}</Text>
                            </Pressable>
                        ))}
                    </View>
                    <View style={{ flex: 1 }} />
                    {/* The flag follows the PM to the Weekly view, where it
                        explains why a code has no dates to revise. */}
                    {sub === 'weekly' && !validation.ok && validation.message ? (
                        <Pressable onPress={() => setSub('baseline')} style={{ cursor: 'pointer' }}>
                            <Text style={{
                                fontSize: 11, fontWeight: '700', color: '#92400E', backgroundColor: '#FFFBEB',
                                border: '1px solid #FDE68A', borderRadius: 999,
                                paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10,
                            }}
                            >
                                {`Schedule detail is short of ${required.label.toLowerCase()} — review on Baseline`}
                            </Text>
                        </Pressable>
                    ) : null}
                </View>
                <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                    {sub === 'baseline'
                        ? 'Import the project schedule, review its logic, and map its activities onto the budget’s cost codes.'
                        : 'Revise and save the plan each week — the field report paces against the latest save.'}
                </Text>
            </View>

            {errors.page ? (
                <Text style={{ fontSize: 12, color: '#B91C1C', marginBottom: 8 }}>{errors.page}</Text>
            ) : null}

            {sub === 'baseline' && (
                <View style={{ minWidth: 520 }}>
                    {/* The mapping panel lives in a review modal that opens
                        on upload. Both wrappers render either way — only
                        their styles change — so opening and closing the
                        modal never remounts the panel and loses unsaved
                        edits, and the review grid keeps seeing live rows. */}
                    <div style={review ? backdrop : { display: 'none' }}>
                        <div style={review ? sheet : undefined}>
                            <ScheduleMapping
                                buId={buId}
                                baseline={data.baseline}
                                budgetPhases={budgetPhases}
                                activities={data.activities}
                                quantity={data.quantity}
                                userName={userName || userEmail}
                                loading={loading}
                                disciplineMap={disciplineMap}
                                onRowsChange={setMappingRows}
                                controllerRef={mappingApi}
                                onSaved={refresh}
                                forceOpen={review}
                                onClose={review ? () => setReview(false) : undefined}
                            />
                        </div>
                    </div>

                    {/* The review workspace owns the schedule import (its
                        header chip and modal replace the old upload card) and
                        the grid+Gantt. Cost code and quantity edits write
                        through the mapping panel above, same as before. */}
                    <ScheduleBaselineReview
                        activities={data.activities}
                        loading={loading}
                        mappingRows={mappingRows}
                        onAssign={assignPhase}
                        onQty={setPhaseQty}
                        activeImport={activeOf('schedule')}
                        onFile={(f, e) => doUpload('schedule', f, e)}
                        busy={busy === 'schedule'}
                        uploadError={errors.schedule}
                        uploadNotice={notices.schedule}
                        onReview={() => setReview(true)}
                        contractDate={contractMC}
                        jobLabel={jobLabel}
                    />
                </View>
            )}

            {sub === 'weekly' && (
                <WeeklyUpdate
                    buId={buId}
                    imports={data.imports}
                    activities={data.activities}
                    baseline={data.baseline}
                    quantity={data.quantity}
                    userName={userName || userEmail}
                    loading={loading}
                    viewImportId={viewImportId}
                    onViewImport={setViewImportId}
                    onSaved={refresh}
                />
            )}
        </View>
    );
};

export default BaselineWeekly;
